import React from 'react';
import { useState } from 'react';
import { RxChevronDown } from 'react-icons/rx';
import { motion } from 'framer-motion';
import { Button } from '../Buttons/DefaultButton';
import type { ButtonProps } from '../Buttons/DefaultButton/Button';
import CapCenterLogo from './CapCenterLogo.png';
import './MegaMenuV2.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faPhone, faEnvelope, faLocationDot } from '@fortawesome/free-solid-svg-icons'

type ImageProps = {
  url?: string;
  src: string;
  alt: string;
};

type MegaMenuLink = {
  url: string;
  title: string | React.ReactNode;
  description: string;
  customHtml?: React.ReactNode;
  button?: ButtonProps;
};

type CategoryLink = {
  title: string;
  links: MegaMenuLink[];
};

type MegaMenuLinkProps = {
  categoryLinks: CategoryLink[];
};

type LinkProps = {
  title: string;
  url: string;
  megaMenu?: MegaMenuLinkProps;
};

type Props = {
  logo: ImageProps;
  links: LinkProps[];
  linksRight: LinkProps[];
  buttons: ButtonProps[];
};

export type Navbar5Props = React.ComponentPropsWithoutRef<'section'> & Partial<Props>;

export const Navbar5 = (props: Navbar5Props) => {
  const { logo, links, linksRight, buttons } = {
    ...Navbar5Defaults,
    ...props,
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 991px)").matches
  )

  React.useEffect(() => {
    window
    .matchMedia("(max-width: 991px)")
    .addEventListener('change', e => setIsMobile( e.matches ));
  }, []);

  return (
    <section
      id="relume"
      className="sticky flex w-full items-center justify-between border-border-primary bg-background-primary lg:min-h-18 px-[2%]"
      style={{ top: 0, zIndex: 9999, backgroundColor: 'white', boxShadow: '0 2px 5px #0003', paddingLeft: "2%", paddingRight: "2%" }}
    >
      <div className="size-full lg:flex lg:items-center lg:justify-between"
        style={{lineHeight: "27px"}}>
        <div className="lg:flex">
          <div className="flex min-h-16 items-center justify-between px-[5%] md:min-h-18 lg:min-h-full lg:px-0">
            <a href={logo.url} style={{ width: '180px' }}>
              <img src={logo.src} alt={logo.alt} />
            </a>
            <button className="-mr-2 flex size-12 flex-col items-center justify-center lg:hidden" onClick={() => setIsMobileMenuOpen(prev => !prev)}>
              <motion.span
                className="my-[3px] h-0.5 w-6 bg-black"
                animate={isMobileMenuOpen ? ['open', 'rotatePhase'] : 'closed'}
                variants={topLineVariants}
              />
              <motion.span className="my-[3px] h-0.5 w-6 bg-black" animate={isMobileMenuOpen ? 'open' : 'closed'} variants={middleLineVariants} />
              <motion.span
                className="my-[3px] h-0.5 w-6 bg-black"
                animate={isMobileMenuOpen ? ['open', 'rotatePhase'] : 'closed'}
                variants={bottomLineVariants}
              />
            </button>
          </div>
          <motion.div
            variants={{
              open: {
                height: 'var(--height-open, 100dvh)',
              },
              close: {
                height: 'var(--height-closed, 0)',
              },
            }}
            initial="close"
            exit="close"
            animate={isMobileMenuOpen ? 'open' : 'close'}
            transition={{ duration: 0.4 }}
            className="overflow-auto px-[5%] lg:ml-3 lg:flex lg:items-center lg:px-0 lg:[--height-closed:auto] lg:[--height-open:auto]"
          >
            {links.map((link, index) => (
              <div className="fontWeight:500" style={{ fontFamily: 'proxima-nova, sans-serif', fontSize: '16px' }}>
                {link.megaMenu ? (
                  <SubMenu key={index} megaMenu={link.megaMenu} title={link.title} isMobile={isMobile} />
                ) : (
                  <a key={index} href={link.url} className="block py-3 text-md first:pt-7 lg:px-4 lg:py-6 lg:text-base first:lg:pt-6">
                    {link.title}
                  </a>
                )}
              </div>
            ))}
            <div
              className="mt-6 flex w-full flex-col gap-y-4 pb-24 lg:hidden lg:pb-0"
              style={{ fontFamily: 'Inter, sans-serif', textDecoration: 'none', color: 'rgb(50, 51, 51)', fontWeight: 500, fontSize: 14 }}
            >
              {linksRight.map((link, index) =>
                link.megaMenu ? (
                  <SubMenu key={index} megaMenu={link.megaMenu} title={link.title} isMobile={isMobile} />
                ) : (
                  <a
                    key={index}
                    href={link.url}
                    className="block py-3 text-md first:pt-7 lg:px-4 lg:py-6 lg:text-base first:lg:pt-6"
                    style={{ fontFamily: 'Inter, sans-serif', textDecoration: 'none', color: 'rgb(50, 51, 51)', fontWeight: 550, fontSize: 14 }}
                  >
                    {link.title}
                  </a>
                ),
              )}
              {buttons.map((button, index) => (
                <Button
                  key={index}
                  href={button.href}
                  rounded={true}
                  resetText={true}
                  label={button.title}
                  style={{
                    backgroundColor: 'rgb(234, 112, 27)',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    border: '0px',
                    fontSize: 15,
                    fontWeight: 500,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
        <div
          className="hidden lg:flex lg:gap-4 pt-2"
          style={{ fontFamily: 'Inter, sans-serif', textDecoration: 'none', color: 'rgb(50, 51, 51)', fontWeight: 400, fontSize: 14 }}
        >
          {linksRight.map((link, index) =>
            link.megaMenu ? (
              <SubMenu key={index} megaMenu={link.megaMenu} title={link.title} isMobile={isMobile} />
            ) : (
              <div className="py-3 mt-1">
                <a
                  key={index}
                  href={link.url}
                  style={{ color: 'rgb(50, 51, 51)', textDecoration: 'none', fontSize: 14, paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                >
                  {link.title}
                </a>
              </div>
            ),
          )}
          {buttons.map((button, index) => (
            <div key={index} className="py-2">
              <Button
                key={index}
                href={button.href}
                rounded={true}
                resetText={true}
                label={button.title}
                style={{
                  backgroundColor: 'rgb(234, 112, 27)',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  border: '0px',
                  fontSize: 15,
                  fontWeight: 500,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
const SubMenu = ({ title, isMobile, megaMenu }: { title: string; isMobile: boolean; megaMenu: MegaMenuLinkProps }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  return (
    <div onMouseEnter={() => !isMobile && setIsDropdownOpen(true)} onMouseLeave={() => !isMobile && setIsDropdownOpen(false)}>
      <button
        className="flex w-full items-center justify-between text-center text-md lg:w-auto lg:flex-none lg:justify-start lg:text-base"
        onClick={() => setIsDropdownOpen(prev => !prev)}
        style={{ outline: '0px solid #ffffff', padding: "1.5rem 1rem" }}
      >
        <div>{title}</div>
        <motion.span
          variants={{
            rotated: { rotate: 180 },
            initial: { rotate: 0 },
          }}
          animate={isDropdownOpen ? 'rotated' : 'initial'}
          transition={{ duration: 0.3 }}
        >
          <RxChevronDown className='navbarChevronDown' />
        </motion.span>
      </button>
      <motion.div
        variants={{
          open: {
            visibility: 'visible',
            opacity: 1,
            height: 'var(--height-open, auto)',
          },
          close: {
            visibility: 'hidden',
            opacity: '0',
            height: 'var(--height-close, 0)',
          },
        }}
        initial="close"
        exit="close"
        animate={isDropdownOpen ? 'open' : 'close'}
        transition={{ duration: 0.3 }}
        className="bottom-auto left-0 top-full w-full min-w-full max-w-full overflow-hidden bg-background-primary lg:absolute lg:w-[100vw] lg:px-[5%] lg:[--height-close:auto]"
        style={{ zIndex: 9999, backgroundColor: 'white', boxShadow: '0 2px 5px #0003' }}
      >
        <div className="mx-auto flex size-full max-w-full items-center justify-between">
          <div className="w-full lg:flex">
            <div className="grid flex-1 gap-x-8 gap-y-6 py-4 pr-8 md:grid-cols-3 md:px-0 md:py-8 lg:py-8 lg:pr-8">
              {megaMenu.categoryLinks.map((group, index) => (
                <div key={index} className="grid auto-rows-max grid-cols-1 grid-rows-[max-content] gap-y-2 md:gap-y-4">
                  <h4
                    style={{ color: 'rgb(234, 112, 27)', fontFamily: 'proxima-nova, sans-serif', fontSize: '16px', fontWeight: 800 }}
                    className="text-sm font-semibold leading-[1.3]"
                  >
                    {group.title}
                  </h4>
                  {group.links.map((link, index) => (
                    <>
                      <a
                        key={index}
                        href={link.url}
                        className="flex flex-row w-full items-center grid-cols-[max-content_1fr] gap-x-3 py-3 px-2 megaMenuV2DropdownLink"
                        style={{ textDecoration: 'none', color: 'rgb(12, 44, 72)', borderRadius: '10px' }}
                      >
                        {link.customHtml || <></>}
                        <div className="flex flex-col justify-center">
                          <h5 className="font-semibold" style={{ fontSize: 18, fontWeight: 600, fontFamily: 'proxima-nova, sans-serif' }}>
                            {link.title}
                          </h5>
                          <div className="hidden text-sm md:block" style={{ fontSize: 14, fontWeight: 400, fontFamily: 'acumin-pro, sans-serif' }}>
                            {link.description}
                          </div>
                        </div>
                      </a>
                    </>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const Navbar5Defaults: Props = {
  logo: {
    url: '/',
    src: CapCenterLogo,
    alt: 'CapCenter Logo image',
  },
  links: [
    {
      title: 'Mortgage',
      url: '',
      megaMenu: {
        categoryLinks: [
          {
            title: 'Tools',
            links: [
              {
                url: '/apply-now/',
                title: 'Apply Now',
                description: 'Take the first step towards homeownership.',
              },
              {
                url: '/mortgage-calculator/purchase/conventional',
                title: 'Mortgage Quote',
                description: 'Calculate your rate, payment, and closing costs.',
              },
              {
                url: '/mortgage-rates/virginia/richmond/purchase/conventional',
                title: 'Mortgage Rates',
                description: 'View our competitive interest rates.',
              },
            ],
          },
          {
            title: 'Programs',
            links: [
              {
                url: '/buy-a-home/mortgage-products',
                title: 'Loan Programs',
                description: 'Explore our ZERO Closing Cost mortgage programs.',
              },
              {
                url: '#',
                title: 'Rate Programs',
                description: 'Explore programs designed to give you control over your interest rate.',
              },
            ],
          },
          {
            title: 'Learning',
            links: [
              {
                url: '/buy-a-home',
                title: 'Our Process',
                description: "Learn about CapCenter's home buying process.",
              },
              {
                url: '/learning',
                title: 'Learning Center',
                description: 'Learn more about mortgage and real estate.',
              },
            ],
          },
        ],
      },
    },
    {
      title: 'Refinance',
      url: '#',
      megaMenu: {
        categoryLinks: [
          {
            title: 'Tools',
            links: [
              {
                url: '/apply-now/refinance',
                title: 'Apply Now',
                description: 'Take the first step towards saving thousands.',
              },
              {
                url: '/mortgage-calculator/refinance/conventional',
                title: 'Refinance Quote',
                description: 'Calculate your rate, payment, and closing costs.',
              },
              {
                url: '/mortgage-rates/virginia/richmond/refinance/conventional',
                title: 'Refinance Rates',
                description: 'View our competitive interest rates.',
              },
            ],
          },
          {
            title: 'Programs',
            links: [
              {
                url: '/refinance/products',
                title: 'Refinance Programs',
                description: 'Explore our ZERO Closing Cost mortgage programs.',
              },
              {
                url: '/home-equity-loan',
                title: 'Home Equity Loans',
                description: "Tap into your home's equity with a Home Equity Loan or Cash Out Refinance.",
              },
            ],
          },
          {
            title: 'Learning',
            links: [
              {
                url: '/refinance',
                title: 'Our Refinance Process',
                description: 'Learn more about how to refinance with CapCenter.',
              },
              {
                url: '/learning',
                title: 'Learning Center',
                description: 'Learn more about mortgage and real estate.',
              },
            ],
          },
        ],
      },
    },
    {
      title: 'Real Estate',
      url: '',
      megaMenu: {
        categoryLinks: [
          {
            title: 'Tools',
            links: [
              {
                url: '/forms/request-realty',
                title: 'Request an agent',
                description: 'Get started and request a consultation with a CapCenter agent.',
              },
              {
                url: '/homes',
                title: 'Search homes for sale',
                description: 'Search homes for sale in your area.',
              },
              {
                url: '/home-sale-estimate/valuation',
                title: 'Home valuation tool',
                description: 'Understand how much your home is worth.',
              },
            ],
          },
          {
            title: 'Programs',
            links: [
              {
                url: '/best-realtor-agency-buy-home',
                title: 'Buy a home',
                description: 'Buy a home with a CapCenter agent.',
              },
              {
                url: '/best-realtor-agency-sell-home',
                title: 'Sell a home',
                description: 'List your home for 1% with a CapCenter agent.',
              },
            ],
          },
          {
            title: 'Learning',
            links: [
              {
                url: '/find-best-local-realtor',
                title: 'Our agents',
                description: 'Meet our expert, full-service agents.',
              },
              {
                url: '/learning',
                title: 'Learning Center',
                description: 'Learn more about mortgage and real estate.',
              },
            ],
          },
        ],
      },
    },
    {
      title: 'Insurance',
      url: '',
      megaMenu: {
        categoryLinks: [
          {
            title: 'Tools',
            links: [
              {
                url: '',
                title: "Home Owner's Insurance Quote",
                description: 'Get an instant quote for home insurance.',
              },
              {
                url: '',
                title: 'Auto Insurance Quote',
                description: 'Get an instant quote for car insurance.',
              },
              {
                url: '',
                title: 'Insurance Portal Login',
                description: 'Manage your insurance policies.',
              },
            ],
          },
          {
            title: 'Programs',
            links: [
              {
                url: '',
                title: 'Our Insurance Offerings',
                description: "Learn about CapCenter's insurance offerings.",
              },
            ],
          },
          {
            title: 'Learning',
            links: [
              {
                url: '',
                title: 'Learning Center',
                description: 'Learn more about insurance topics and trends.',
              },
            ],
          },
        ],
      },
    },
  ],
  linksRight: [
    {
      title: 'Contact',
      url: '',
      megaMenu: {
        categoryLinks: [
          {
            title: 'Contact Us',
            links: [
              {
                url: 'tel:+18009685000',
                title: <div className='items-center'><FontAwesomeIcon icon={faPhone} className='pr-1' />(800) 968-5000</div>,
                description: 'Give us a call and receive immediate support.',
              },
              {
                url: 'mailto:customerservice@capcenter.com',
                title: <div className='items-center'><FontAwesomeIcon icon={faEnvelope} className='pr-1' />customerservice@capcenter.com</div>,
                description: 'Our customer support team is available to assist you over email.',
              },
              {
                url: '#',
                title: <div className='items-center'><FontAwesomeIcon icon={faLocationDot} className='pr-1' />Locations</div>,
                description: 'Contact a specific CapCenter office and view our hours of operations.',
              },
            ],
          },
          {
            title: 'About us',
            links: [
              {
                url: '#',
                title: 'Our story',
                description: 'Learn more about CapCenter and Zero Closing Costs.',
              },
              {
                url: '#',
                title: 'Service area',
                description: 'View where we offer Mortgage and Real Estate services.',
              },
              {
                url: '#',
                title: 'Testimonials',
                description: 'See what our clients are saying about us.',
              },
            ],
          },
          {
            title: 'Social Media',
            links: [
              {
                url: 'https://www.facebook.com/CapCenterLLC',
                title: 'Facebook',
                customHtml: <FontAwesomeIcon style={{ height:"45px", width: 'auto' }} className="px-1" icon={faFacebook} />,
                description: 'Follow our Facebook for recent listings and updates.',
              },
              {
                url: 'https://www.instagram.com/capcenter/',
                title: 'Instagram',
                customHtml: <FontAwesomeIcon style={{ height:"45px", width: 'auto' }} className="px-1" icon={faInstagram} />,
                description: 'Follow our instagram for recent image and reel postings',
              },
              // {
              //   url: "https://www.youtube.com/channel/UCoFRaNzAh-9NkVRxe2eTNwA",
              //   title: "Youtube",
              //   customHtml: <FontAwesomeIcon style={{width: "50px", height: "auto"}} icon={faYoutube} />,
              //   description: "Follow our youtube for video updates and reels",
              // },
            ],
          },
        ],
      },
    },
    { title: 'Log In', url: '/sign-in' },
    { title: 'Español', url: '/espanol/hipoteca/comprar-casa' },
  ],
  buttons: [
    {
      title: 'Apply Now',
      size: 'sm',
      href: '/apply-now/',
    },
  ],
};

const topLineVariants = {
  open: {
    translateY: 8,
    transition: { delay: 0.1 },
  },
  rotatePhase: {
    rotate: -45,
    transition: { delay: 0.2 },
  },
  closed: {
    translateY: 0,
    rotate: 0,
    transition: { duration: 0.2 },
  },
};

const middleLineVariants = {
  open: {
    width: 0,
    transition: { duration: 0.1 },
  },
  closed: {
    width: '1.5rem',
    transition: { delay: 0.3, duration: 0.2 },
  },
};

const bottomLineVariants = {
  open: {
    translateY: -8,
    transition: { delay: 0.1 },
  },
  rotatePhase: {
    rotate: 45,
    transition: { delay: 0.2 },
  },
  closed: {
    translateY: 0,
    rotate: 0,
    transition: { duration: 0.2 },
  },
};
